import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";
import {
  HrCurrencyListDocument,
  HrEmployeeCompensationGetByIdDocument,
  HrEmployeeCompensationHistoryListDocument,
  HrEmployeeCompensationLastHistoryDeleteDocument,
  HrEmployeeCompensationLastHistoryUpdateDocument,
  HrEmployeeCompensationListDocument,
  HrEmployeeCompensationUpdateBulkDocument,
  HrEmployeeCompensationUpdateDocument,
} from "api/generated";

import {
  IHrCurrencyListModel,
  IHrEmployeeCompensationHistoryListModel,
  IHrEmployeeCompensationLastHistoryUpdateVars,
  IHrEmployeeCompensationModel,
  IHrEmployeeCompensationUpdateBulkVars,
  IHrEmployeeCompensationUpdateVars,
} from "./types";

export async function getHrCurrencyList(): Promise<IHrCurrencyListModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrCurrencyListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.hrCurrencyList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getHrEmployeeCompensationHistoryList(
  employeeId: number
): Promise<IHrEmployeeCompensationHistoryListModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrEmployeeCompensationHistoryListDocument,
      variables: {
        employeeId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.hrEmployeeCompensationHistoryList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getHrEmployeeCompensationList(): Promise<
  IHrEmployeeCompensationModel[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrEmployeeCompensationListDocument,
      fetchPolicy: "network-only",
    });

    return res?.data?.hrEmployeeCompensationList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getHrEmployeeCompensationByEmployee(
  employeeId: number
): Promise<IHrEmployeeCompensationModel> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrEmployeeCompensationGetByIdDocument,
      variables: {
        employeeId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.hrEmployeeCompensationGetById ?? {};
  } catch (error) {
    throw error;
  }
}

export async function updateHrEmployeeCompensation(
  input: IHrEmployeeCompensationUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrEmployeeCompensationUpdateDocument,
      variables: input,
      fetchPolicy: "no-cache",
    });

    // Evict compensation data for the updated employee
    executeEviction(client, {
      typename: "HrEmployeeCompensation",
      fieldNames: [
        "hrEmployeeCompensationList",
        "hrEmployeeCompensationGetById",
        "hrEmployeeCompensationHistoryList",
      ],
    });

    return res?.data?.hrEmployeeCompensationUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateBulkHrEmployeeCompensation(
  input: IHrEmployeeCompensationUpdateBulkVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrEmployeeCompensationUpdateBulkDocument,
      variables: input,
      fetchPolicy: "no-cache",
    });

    // Evict all compensation data after bulk update
    executeEviction(client, {
      typename: "HrEmployeeCompensation",
      fieldNames: [
        "hrEmployeeCompensationList",
        "hrEmployeeCompensationGetById",
        "hrEmployeeCompensationHistoryList",
      ],
    });

    return res?.data?.hrEmployeeCompensationUpdateBulk ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateHrEmployeeCompensationLastHistory(
  input: IHrEmployeeCompensationLastHistoryUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrEmployeeCompensationLastHistoryUpdateDocument,
      variables: input,
      fetchPolicy: "no-cache",
    });

    // Evict compensation history data after update
    executeEviction(client, {
      typename: "HrEmployeeCompensation",
      fieldNames: [
        "hrEmployeeCompensationHistoryList",
        "hrEmployeeCompensationGetById",
      ],
    });

    return res?.data?.hrEmployeeCompensationLastHistoryUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function deleteHrEmployeeCompensationLastHistory(
  employeeId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrEmployeeCompensationLastHistoryDeleteDocument,
      variables: {
        employeeId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict compensation history data after deletion
    executeEviction(client, {
      typename: "HrEmployeeCompensation",
      fieldNames: [
        "hrEmployeeCompensationHistoryList",
        "hrEmployeeCompensationGetById",
      ],
    });

    return res?.data?.hrEmployeeCompensationLastHistoryDelete ?? false;
  } catch (error) {
    throw error;
  }
}
